import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, TextField, Button, Paper, Rating, CircularProgress, Alert, Avatar, Chip, Tabs, Tab, LinearProgress, Link, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Comment, CommentType } from '../types/comment';
import { commentService } from '../services/commentService';
import { useAuth } from '../context/AuthContext';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ReplyIcon from '@mui/icons-material/Reply';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { useNavigate } from 'react-router-dom';
import RecaptchaNotice from './common/RecaptchaNotice';

interface CommentsSectionProps {
    businessId: string;
    ownerEmail?: string;
    // False when the listing has no business email: nobody would be told about a
    // question, so "Ask a question" is turned off (existing answers still show).
    questionsEnabled?: boolean;
}

const RECAPTCHA_SITE_KEY = '6LeDbCEsAAAAANw-bAoYe_8Ldw4phCZQLjkkl_Hw';

// Matches the 14px text used across the rest of the listing page. Inputs stay
// at 16px on phones: iOS Safari zooms the page when a focused field is smaller.
const fieldTextSx = {
    '& .MuiInputBase-input': { fontSize: { xs: '16px', md: '14px' } },
    '& .MuiInputLabel-root': { fontSize: '14px' },
    '& .MuiInputLabel-shrink': { fontSize: '16px' },
    '& .MuiFormHelperText-root': { fontSize: '12px' },
};

const cardSx = { borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.08)' };

const isRtl = (text?: string | null) => /[؀-ۿ]/.test(text || '');

// Posts are shown as plain text, never as HTML. Older comments imported from the
// WordPress site contain <p>/<br> tags, so those become line breaks first.
const toPlainText = (value?: string | null) => {
    const text = String(value || '');
    if (!/[<&]/.test(text)) return text;
    const withBreaks = text.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>\s*/gi, '\n\n');
    try {
        const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
        return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
    } catch {
        return withBreaks.replace(/<[^>]+>/g, '').trim();
    }
};

const isQuestion = (c: Comment) => c.type === 'question';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = { name?: string; email?: string; comment?: string; rating?: string };

const CommentsSection: React.FC<CommentsSectionProps> = ({ businessId, ownerEmail, questionsEnabled = true }) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<{ severity: 'success' | 'info' | 'warning'; text: string } | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [recaptchaReady, setRecaptchaReady] = useState(false);
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyText, setReplyText] = useState('');
    const [replySubmitting, setReplySubmitting] = useState(false);
    // `tab` = which list is shown; `mode` = what the form submits (review or question).
    const [tab, setTab] = useState<CommentType>('review');
    const [mode, setMode] = useState<CommentType>('review');

    const { user } = useAuth();
    const navigate = useNavigate();

    // Newest first, for both reviews and questions.
    const newestFirst = (a: Comment, b: Comment) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    const reviews = useMemo(() => comments.filter(c => !isQuestion(c)).sort(newestFirst), [comments]);
    const questions = useMemo(() => comments.filter(isQuestion).sort(newestFirst), [comments]);

    const ratingSummary = useMemo(() => {
        const rated = reviews.map(r => r.rating || 0).filter(r => r >= 1 && r <= 5);
        const counts = [5, 4, 3, 2, 1].map(star => ({ star, count: rated.filter(r => r === star).length }));
        const average = rated.length ? rated.reduce((a, r) => a + r, 0) / rated.length : 0;
        return { total: rated.length, average, counts };
    }, [reviews]);

    // Plain React state (no form library): each field only updates when its
    // value actually changes, so nothing can feed back into a render loop.
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [commentText, setCommentText] = useState('');
    const [rating, setRating] = useState<number | null>(null); // no default: nobody should rate by accident
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    // Prefill name/email for logged-in users, without overwriting what they typed.
    useEffect(() => {
        if (!user) return;
        setName(prev => prev || user.name || '');
        setEmail(prev => prev || user.email || '');
    }, [user?.name, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

    const validate = (): FieldErrors => {
        const errs: FieldErrors = {};
        if (!name.trim()) errs.name = 'نام را وارد کنید';
        if (!email.trim()) errs.email = 'ایمیل را وارد کنید';
        else if (!EMAIL_RE.test(email.trim())) errs.email = 'ایمیل معتبر وارد کنید';
        if (!commentText.trim()) errs.comment = mode === 'question' ? 'متن پرسش را وارد کنید' : 'متن نظر را وارد کنید';
        else if (commentText.length > 3000) errs.comment = 'حداکثر ۳۰۰۰ نویسه';
        if (mode === 'review' && !(rating && rating >= 1 && rating <= 5)) errs.rating = 'لطفاً امتیاز دهید';
        return errs;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const errs = validate();
        setFieldErrors(errs);
        if (Object.keys(errs).length > 0) return;

        if (!recaptchaReady) {
            setError('لطفاً چند لحظه صبر کنید و دوباره تلاش کنید.');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            setNotice(null);

            // Execute reCAPTCHA v3
            const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'comment' });

            const result = await commentService.create({
                businessId,
                authorName: name.trim(),
                authorEmail: email.trim(),
                content: commentText,
                type: mode,
                rating: mode === 'review' ? rating : null,
                captchaToken: token
            });

            if (result.status === 'rejected') {
                setError(result.message);
                return;
            }

            if (result.status === 'approved' && result.comment) {
                setComments(prev => [result.comment as Comment, ...prev]);
            }
            setTab(result.type);
            if (result.convertedToQuestion) {
                setMode('question');
                setNotice({ severity: 'info', text: `${result.message} متن شما یک پرسش بود، برای همین در بخش «پرسش و پاسخ» قرار گرفت و امتیازی برای آن ثبت نشد.` });
            } else {
                setNotice({ severity: result.status === 'approved' ? 'success' : 'info', text: result.message });
            }
            setCommentText('');
            setRating(null);
        } catch (err: any) {
            setError(err.response?.data?.message || err.response?.data?.error || 'ارسال ناموفق بود.');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        // Load reCAPTCHA v3 script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            window.grecaptcha.ready(() => {
                setRecaptchaReady(true);
            });
        };
        document.head.appendChild(script);

        const fetchComments = async () => {
            try {
                setLoading(true);
                const data = await commentService.getByBusinessId(businessId, user?.email);
                setComments(data);
                // Open on Q&A when a listing only has questions so far.
                if (!data.some(c => !isQuestion(c)) && data.some(isQuestion)) { setTab('question'); if (questionsEnabled) setMode('question'); }
            } catch (err) {
                setError('بارگذاری نظرات ناموفق بود.');
            } finally {
                setLoading(false);
            }
        };

        fetchComments();

        return () => {
            const scripts = document.querySelectorAll(`script[src*="recaptcha/api.js"]`);
            scripts.forEach(s => s.remove());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    const switchMode = (next: CommentType) => {
        setMode(next);
        setError(null);
        setNotice(null);
        setFieldErrors({});
    };

    // Viewing a list also points the form at the same kind of post.
    const switchTab = (next: CommentType) => {
        setTab(next);
        setMode(next);
        setError(null);
        setNotice(null);
        setFieldErrors({});
    };

    if (loading) {
        return <CircularProgress />;
    }

    const isCurrentUserOwner = Boolean(user && ownerEmail && user.email.toLowerCase() === ownerEmail.toLowerCase());
    const canReply = isCurrentUserOwner || user?.role === 'admin';

    const handleLike = async (comment: Comment) => {
        if (!user) {
            navigate('/login');
            return;
        }
        try {
            const result = await commentService.like(comment.id, user.email);
            setComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, isLiked: result.liked, likesCount: (c.likesCount || 0) + (result.liked ? 1 : -1) }
                    : c
            ));
        } catch (err) {
            console.error('Failed to like comment:', err);
        }
    };

    const handleReplySubmit = async (comment: Comment) => {
        if (!user || !replyText.trim()) return;
        try {
            setReplySubmitting(true);
            const updated = await commentService.reply(comment.id, user.email, replyText);
            setComments(prev => prev.map(c => c.id === comment.id ? { ...c, reply: updated.reply, replyAt: updated.replyAt } : c));
            setReplyingTo(null);
            setReplyText('');
        } catch (err) {
            console.error('Failed to post reply:', err);
        } finally {
            setReplySubmitting(false);
        }
    };

    const authorChips = (comment: Comment) => (
        <>
            {comment.isListingOwner && (
                <Chip
                    label="صاحب کسب‌وکار"
                    size="small"
                    icon={<VerifiedIcon sx={{ fontSize: '0.8rem !important' }} />}
                    sx={{ ml: 1, height: 22, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: '#2854a1', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                />
            )}
            {comment.isAuthorVerified && !comment.isListingOwner && (
                <Chip
                    label="کاربر تأییدشده"
                    size="small"
                    icon={<VerifiedUserIcon sx={{ fontSize: '0.8rem !important' }} />}
                    sx={{ ml: 1, height: 22, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', '& .MuiChip-icon': { color: '#166534' } }}
                />
            )}
        </>
    );

    const textBlock = (text: string, extraSx: object = {}) => (
        <Typography
            component="div"
            sx={{
                color: '#475569',
                fontSize: '14px',
                lineHeight: 1.7,
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
                textAlign: isRtl(text) ? 'right' : 'left',
                direction: isRtl(text) ? 'rtl' : 'ltr',
                ...extraSx
            }}
        >
            {text}
        </Typography>
    );

    const replyEditor = (comment: Comment) => replyingTo === comment.id && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
            <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={isQuestion(comment) ? 'پاسخ خود را به این پرسش بنویسید...' : 'پاسخ خود را به عنوان صاحب کسب‌وکار بنویسید...'}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                sx={{ bgcolor: 'white', mb: 2, ...fieldTextSx }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setReplyingTo(null)}>انصراف</Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleReplySubmit(comment)}
                    disabled={replySubmitting || !replyText.trim()}
                >
                    {replySubmitting ? 'در حال ارسال...' : 'ارسال پاسخ'}
                </Button>
            </Box>
        </Box>
    );

    const replyButton = (comment: Comment) => canReply && (
        <Button
            size="small"
            startIcon={<ReplyIcon />}
            onClick={() => {
                setReplyText(comment.reply || '');
                setReplyingTo(replyingTo === comment.id ? null : comment.id);
            }}
            sx={{ textTransform: 'none' }}
        >
            {comment.reply ? 'ویرایش پاسخ' : isQuestion(comment) ? 'پاسخ دادن' : 'پاسخ'}
        </Button>
    );

    const renderReview = (comment: Comment) => {
        const content = toPlainText(comment.content);
        return (
            <Paper key={comment.id} sx={{ p: { xs: 2, md: 2.5 }, mb: 2, ...cardSx }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32, fontSize: '0.9rem' }}>
                            {comment.authorName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.3, fontSize: '14px' }}>
                                {comment.authorName}
                                {authorChips(comment)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(comment.createdAt).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                    {comment.rating ? <Rating value={comment.rating} readOnly size="small" /> : null}
                </Box>

                {textBlock(content, { mt: 1.5, mb: 1.5 })}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Button
                        size="small"
                        startIcon={comment.isLiked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                        onClick={() => handleLike(comment)}
                        sx={{ color: comment.isLiked ? 'primary.main' : 'text.secondary', textTransform: 'none', fontWeight: comment.isLiked ? 'bold' : 'normal' }}
                    >
                        {comment.likesCount || 0} پسند
                    </Button>
                    {replyButton(comment)}
                </Box>

                {comment.reply && (
                    <Box sx={{ mt: 3, ml: { xs: 2, md: 4 }, p: 2, bgcolor: '#f8fafc', borderRadius: 2, borderRight: '4px solid #2854a1' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2854a1', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReplyIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} />
                                پاسخ کسب‌وکار
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {comment.replyAt ? new Date(comment.replyAt).toLocaleDateString() : ''}
                            </Typography>
                        </Box>
                        {textBlock(comment.reply, { fontStyle: 'italic' })}
                    </Box>
                )}
                {replyEditor(comment)}
            </Paper>
        );
    };

    const renderQuestion = (comment: Comment) => {
        const content = toPlainText(comment.content);
        return (
            <Paper key={comment.id} sx={{ p: { xs: 2, md: 2.5 }, mb: 2, ...cardSx }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Avatar sx={{ bgcolor: '#eef2ff', color: '#2854a1', width: 32, height: 32, fontSize: '0.95rem', fontWeight: 'bold' }}>
                        س
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {textBlock(content, { color: '#1e293b', fontWeight: 600 })}
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            {comment.authorName}{authorChips(comment)} · {new Date(comment.createdAt).toLocaleDateString()}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 2 }}>
                    <Avatar sx={{ bgcolor: comment.reply ? '#2854a1' : '#f1f5f9', color: comment.reply ? 'white' : '#94a3b8', width: 32, height: 32, fontSize: '0.95rem', fontWeight: 'bold' }}>
                        ج
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {comment.reply ? (
                            <>
                                {textBlock(comment.reply)}
                                <Typography variant="caption" sx={{ color: '#2854a1', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <VerifiedIcon sx={{ fontSize: 14 }} />
                                    پاسخ صاحب کسب‌وکار
                                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
                                        {comment.replyAt ? ` · ${new Date(comment.replyAt).toLocaleDateString()}` : ''}
                                    </Box>
                                </Typography>
                            </>
                        ) : (
                            <Typography sx={{ color: 'text.secondary', fontSize: '13px', pt: 0.75 }}>هنوز پاسخی ثبت نشده است.</Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                    <Button
                        size="small"
                        startIcon={comment.isLiked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                        onClick={() => handleLike(comment)}
                        sx={{ color: comment.isLiked ? 'primary.main' : 'text.secondary', textTransform: 'none', fontWeight: comment.isLiked ? 'bold' : 'normal' }}
                    >
                        این سوال من هم هست{comment.likesCount ? ` (${comment.likesCount})` : ''}
                    </Button>
                    {replyButton(comment)}
                </Box>
                {replyEditor(comment)}
            </Paper>
        );
    };

    const ratingSummaryBlock = ratingSummary.total > 0 && (
        <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2, ...cardSx, display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', minWidth: 96 }}>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>{ratingSummary.average.toFixed(1)}</Typography>
                <Rating value={ratingSummary.average} precision={0.1} readOnly size="small" />
                <Typography variant="caption" color="text.secondary" component="div">{ratingSummary.total} نظر</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 180 }}>
                {ratingSummary.counts.map(({ star, count }) => (
                    <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                        <Typography sx={{ fontSize: '12px', width: 12, textAlign: 'center' }}>{star}</Typography>
                        <StarIcon sx={{ fontSize: 13, color: '#faaf00' }} />
                        <LinearProgress
                            variant="determinate"
                            value={ratingSummary.total ? (count / ratingSummary.total) * 100 : 0}
                            sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#faaf00', borderRadius: 3 } }}
                        />
                        <Typography sx={{ fontSize: '12px', width: 20, color: 'text.secondary' }}>{count}</Typography>
                    </Box>
                ))}
            </Box>
        </Paper>
    );

    const form = (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, ...cardSx, ...fieldTextSx }}>
            <ToggleButtonGroup
                value={mode}
                exclusive
                size="small"
                onChange={(_e: unknown, value: CommentType | null) => { if (value) switchMode(value); }}
                sx={{ mb: 1.5, '& .MuiToggleButton-root': { px: 2, gap: 0.75, fontSize: '13.5px', fontWeight: 'bold', textTransform: 'none' } }}
            >
                <ToggleButton value="review"><StarIcon sx={{ fontSize: 17 }} />ثبت نظر و امتیاز</ToggleButton>
                <ToggleButton value="question" disabled={!questionsEnabled}><HelpOutlineIcon sx={{ fontSize: 17 }} />پرسیدن سوال</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mb: 1.5 }}>
                {mode === 'review' ? (
                    <>
                        از تجربه خود با این کسب‌وکار بنویسید.{' '}
                        {questionsEnabled ? (
                            <>
                                فقط سوال دارید؟{' '}
                                <Link component="button" type="button" onClick={() => switchMode('question')} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
                                    سوال خود را بپرسید
                                </Link>
                            </>
                        ) : (
                            'پرسیدن سوال برای این کسب‌وکار فعال نیست، چون ایمیلی برای آن ثبت نشده است.'
                        )}
                    </>
                ) : (
                    'صاحب کسب‌وکار به پرسش شما پاسخ می‌دهد و پاسخ برای بقیه بازدیدکنندگان هم نمایش داده می‌شود.'
                )}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {notice && <Alert severity={notice.severity} sx={{ mb: 2 }}>{notice.text}</Alert>}

            <form onSubmit={handleSubmit} noValidate>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    {mode === 'review' && (
                        <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography component="legend" sx={{ fontSize: '14px' }}>امتیاز شما:</Typography>
                            <Rating
                                name="rating"
                                value={rating}
                                onChange={(_event: unknown, newValue: number | null) => {
                                    setRating(newValue);
                                    if (newValue) setFieldErrors(prev => ({ ...prev, rating: undefined }));
                                }}
                            />
                            {fieldErrors.rating && (
                                <Typography sx={{ color: 'error.main', fontSize: '12px' }}>{fieldErrors.rating}</Typography>
                            )}
                        </Box>
                    )}
                    <Box>
                        <TextField
                            fullWidth
                            id="name"
                            name="name"
                            label="نام شما"
                            value={name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                            error={Boolean(fieldErrors.name)}
                            helperText={fieldErrors.name}
                            size="small"
                        />
                    </Box>
                    <Box>
                        <TextField
                            fullWidth
                            id="email"
                            name="email"
                            label="ایمیل (نمایش داده نمی‌شود)"
                            type="email"
                            value={email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                            error={Boolean(fieldErrors.email)}
                            helperText={fieldErrors.email}
                            size="small"
                        />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <TextField
                            fullWidth
                            id="comment"
                            name="comment"
                            label={mode === 'review' ? 'نظر شما' : 'پرسش شما'}
                            multiline
                            rows={3}
                            size="small"
                            value={commentText}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCommentText(e.target.value)}
                            error={Boolean(fieldErrors.comment)}
                            helperText={fieldErrors.comment || 'به فارسی یا انگلیسی بنویسید.'}
                        />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <RecaptchaNotice mt={0} />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={submitting || !recaptchaReady}
                            sx={{ mt: 0.5, px: 2.5, fontSize: '14px' }}
                        >
                            {submitting ? 'در حال ارسال...' : !recaptchaReady ? 'در حال بارگذاری...' : mode === 'review' ? 'ثبت نظر' : 'ارسال پرسش'}
                        </Button>
                    </Box>
                </Box>
            </form>
        </Paper>
    );

    // Only tabs that have something in them are shown.
    const hasReviews = reviews.length > 0;
    const hasQuestions = questions.length > 0;
    const view: CommentType | null = hasReviews && hasQuestions ? tab : hasReviews ? 'review' : hasQuestions ? 'question' : null;
    const headingSx = { fontWeight: 'bold', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1, mb: 2 };

    return (
        <Box sx={{ mt: 4 }}>
            {hasReviews && hasQuestions ? (
                <Tabs
                    value={tab}
                    onChange={(_e: unknown, value: CommentType) => switchTab(value)}
                    variant="fullWidth"
                    sx={{ mb: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', '& .MuiTab-root': { fontSize: '14px', fontWeight: 'bold', minHeight: 48 } }}
                >
                    <Tab value="review" icon={<StarIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`نظرات (${reviews.length})`} />
                    <Tab value="question" icon={<QuestionAnswerOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`پرسش و پاسخ (${questions.length})`} />
                </Tabs>
            ) : (
                <Typography variant="h6" component="h2" sx={headingSx}>
                    {view === 'question'
                        ? <><QuestionAnswerOutlinedIcon sx={{ fontSize: 20 }} />پرسش و پاسخ ({questions.length})</>
                        : view === 'review'
                            ? <><StarIcon sx={{ fontSize: 20, color: '#faaf00' }} />نظرات ({reviews.length})</>
                            : 'نظرات و پرسش‌ها'}
                </Typography>
            )}

            {view === 'review' && ratingSummaryBlock}
            {form}

            {view === 'review' && reviews.map(renderReview)}
            {view === 'question' && questions.map(renderQuestion)}
            {view === null && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3, fontSize: '14px' }}>
                    هنوز نظر یا پرسشی ثبت نشده است. اولین نفری باشید که نظر می‌دهد یا سوال می‌پرسد!
                </Typography>
            )}
        </Box>
    );
};

export default CommentsSection;
